/**
 * Typed Event Bus — lightweight pub/sub for cross-component communication
 * 
 * Usage:
 *   eventBus.emit('price:updated', { drugId: 'x', price: 1000, name: 'Drug X' });
 *   eventBus.on('price:updated', (data) => { ... });
 */

type Handler<T = unknown> = (data: T) => void;

class EventBus {
  private handlers = new Map<string, Set<Handler>>();

  on<T = unknown>(event: string, handler: Handler<T>): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler as Handler);
    
    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  off<T = unknown>(event: string, handler: Handler<T>): void {
    this.handlers.get(event)?.delete(handler as Handler);
  }

  emit<T = unknown>(event: string, data: T): void {
    this.handlers.get(event)?.forEach(handler => {
      try {
        handler(data);
      } catch (err) {
        console.error(`[EventBus] Error in handler for "${event}":`, err);
      }
    });
  }

  /** Remove all handlers for an event (or all events if no event specified) */
  clear(event?: string): void {
    if (event) {
      this.handlers.delete(event);
    } else {
      this.handlers.clear();
    }
  }
}

// Singleton — persists across hot-reloads via globalThis
const g = globalThis as any;
if (!g.__eventBus) g.__eventBus = new EventBus();

export const eventBus: EventBus = g.__eventBus;
